import React from 'react';
import { ReportSectionComponent } from '@/types';

interface StatusTableProps {
  components: ReportSectionComponent[];
}

type ColKey = 'in' | 'ni' | 'np' | 'o';

const COLS: Array<{ key: ColKey; label: string; title: string }> = [
  { key: 'in', label: 'IN', title: 'Inspected' },
  { key: 'ni', label: 'NI', title: 'Not Inspected' },
  { key: 'np', label: 'NP', title: 'Not Present' },
  { key: 'o',  label: 'O',  title: 'Observations' },
];

const cellKey = (c: ReportSectionComponent): ColKey | null => {
  if (c.hasFindings) return 'o';
  if (c.inspectionStatus === 'inspected') return 'in';
  if (c.inspectionStatus === 'not_inspected') return 'ni';
  if (c.inspectionStatus === 'not_present') return 'np';
  return null;
};

// Per the design system: each status is a different signal — IN is the
// quiet "we looked", NI flags an access issue, NP confirms absence, O links
// the table to the findings below it. Color the active dot to match.
const ACTIVE_CELL_BG: Record<ColKey, string> = {
  in: 'bg-surface-2',
  ni: 'bg-sev-minor-soft',
  np: 'bg-surface-2',
  o:  'bg-brand-soft',
};

const ACTIVE_DOT_BG: Record<ColKey, string> = {
  in: 'bg-ink-muted',
  ni: 'bg-sev-minor',
  np: 'bg-transparent border border-dashed border-ink-faint',
  o:  'bg-brand',
};

const Check: React.FC = () => (
  <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true" className="text-brand-ink">
    <path
      d="M3 7.5 L6 10 L11 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StatusTable: React.FC<StatusTableProps> = ({ components }) => {
  if (components.length === 0) return null;

  return (
    <div className="bg-surface-1 border border-border-subtle rounded-lg overflow-hidden shadow-xs mb-4">
      <div
        className="grid bg-surface-2 border-b border-border-subtle"
        style={{ gridTemplateColumns: '1fr 52px 52px 52px 52px' }}
      >
        <div className="pl-4 py-2 text-[10px] font-bold tracking-wider text-ink-subtle uppercase self-center">
          Component
        </div>
        {COLS.map((col) => (
          <div
            key={col.key}
            title={col.title}
            className="text-center py-2 text-[10px] font-bold tracking-wider text-ink-subtle uppercase"
          >
            {col.label}
          </div>
        ))}
      </div>
      {components.map((c, i) => {
        const active = cellKey(c);
        return (
          <div
            key={c.id}
            className={`grid items-stretch text-sm ${i > 0 ? 'border-t border-border-subtle' : ''}`}
            style={{ gridTemplateColumns: '1fr 52px 52px 52px 52px', minHeight: 40 }}
          >
            <div className="pl-4 py-2 text-ink font-medium self-center">{c.name}</div>
            {COLS.map((col) => {
              const on = active === col.key;
              return (
                <div
                  key={col.key}
                  className={`flex justify-center items-center mx-0.5 my-1 rounded-md ${
                    on ? ACTIVE_CELL_BG[col.key] : ''
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full ${
                      on
                        ? ACTIVE_DOT_BG[col.key]
                        : 'bg-surface-1 ring-[1.5px] ring-border ring-inset'
                    }`}
                  >
                    {on && col.key === 'o' ? <Check /> : null}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
      {/* Visible legend — the column-header tooltips (`title`) only fire on
          hover, which is invisible to mobile/touch readers. Inspector reports
          ship to homebuyers who haven't seen IN/NI/NP/O before; the legend
          is the difference between a status table and a riddle. */}
      <div
        className="border-t border-border-subtle px-4 py-2 bg-surface-2 text-[11px] text-ink-muted flex flex-wrap gap-x-4 gap-y-1"
        aria-label="Status key"
      >
        {COLS.map((col) => (
          <span key={col.key} className="inline-flex items-center gap-1.5">
            <span className="font-bold tracking-wider text-ink">{col.label}</span>
            <span className="text-ink-subtle">{col.title}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default StatusTable;
